import { useEffect } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  FormControl,
  Grid,
  InputLabel,
  Link,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GroupedBarChart from '../components/Dashboard/GroupedBarChart';
import HorizontalBarList from '../components/Dashboard/HorizontalBarList';
import MetricCard from '../components/Dashboard/MetricCard';
import PartnerDonutChart from '../components/Dashboard/PartnerDonutChart';
import SectionCard from '../components/Dashboard/SectionCard';
import { fetchDashboardOverview, setDashboardTimeframe } from '../store/slices/dashboardSlice';

const timeframeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' }
];

const formatNumber = (value) => Number(value || 0).toLocaleString();
const DASHBOARD_REFRESH_INTERVAL = 10 * 60 * 1000;

const DashboardPage = () => {
  const dispatch = useDispatch();
  const { timeframe, data, loading, error, lastUpdated } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardOverview(timeframe));
  }, [dispatch, timeframe]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      dispatch(fetchDashboardOverview(timeframe));
    }, DASHBOARD_REFRESH_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [dispatch, timeframe]);

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        spacing={2}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Typography color="text.primary">Overview</Typography>
          </Breadcrumbs>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
            Ticketing Dashboard
          </Typography>
          <Typography color="text.secondary">
            Snapshot of transactions, tickets, stations, and partner contribution.
          </Typography>
          {lastUpdated ? (
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>
          ) : null}
        </Box>

        <FormControl sx={{ minWidth: { xs: '100%', sm: 220 } }}>
          <InputLabel id="dashboard-timeframe-label">Timeframe</InputLabel>
          <Select
            labelId="dashboard-timeframe-label"
            label="Timeframe"
            value={timeframe}
            onChange={(event) => dispatch(setDashboardTimeframe(event.target.value))}
          >
            {timeframeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading && <LinearProgress />}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container rowSpacing={3} columnSpacing={{ xs: 0, sm: 0, md: 1, lg: 1, xl: 3 }}>
        <Grid item xs={12} md={6}>
          <MetricCard
            title="Total Transaction"
            value={data.summary.totalTransactions}
            accent="blue"
            formatNumber={formatNumber}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricCard
            title="Total Ticket"
            value={data.summary.totalTickets}
            accent="green"
            formatNumber={formatNumber}
          />
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title="Hourly Transaction and Ticket"
            subtitle="Grouped bar view to compare transaction volume and ticket issuance by hour."
          >
            <GroupedBarChart data={data.hourly} formatNumber={formatNumber} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Total Ticket per Origin"
            subtitle="Top origin stations ranked by issued tickets."
          >
            <HorizontalBarList data={data.origin} tone="blue" formatNumber={formatNumber} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Total Ticket per Destination"
            subtitle="Top destination stations ranked by issued tickets."
          >
            <HorizontalBarList data={data.destination} tone="green" formatNumber={formatNumber} />
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard
            title="Total Transaction per Partner"
            subtitle="Partner contribution shown as a donut with exact counts and percentage share."
          >
            <PartnerDonutChart data={data.partners} formatNumber={formatNumber} />
          </SectionCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default DashboardPage;
